import React, { useState } from 'react';

import { Box, Loader, Paper, Stack, Text, TextInput, Title, UnstyledButton } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import useAutoCompleteAppointmentType from 'hooks/appointmentType/useAutoCompleteAppointmentType';

import { useGLMCardStyles } from '../styles';
import { FormValues } from '../types';

interface IKiloGLMAppointmentTypeSelectProps {
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>;
}

const KiloGLMAppointmentTypeSelect = ({ form }: IKiloGLMAppointmentTypeSelectProps): JSX.Element => {
  const { classes, cx } = useGLMCardStyles();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useAutoCompleteAppointmentType({ search: debouncedSearch });
  const appointmentTypes = data?.data?.map(a => ({ value: a.appointmentTypeId, label: a.name })) ?? [];

  const toggleAppointmentType = (id: string, label: string): void => {
    const currentIds = form.values.appointmentTypeIds || [];
    const currentLabels = form.values.appointmentTypeLabels || {};
    if (currentIds.includes(id)) {
      const remainingLabels = { ...currentLabels };
      delete remainingLabels[id];
      form.setValues(prev => ({
        ...prev,
        appointmentTypeIds: currentIds.filter(v => v !== id),
        appointmentTypeLabels: remainingLabels,
      }));
    } else {
      form.setValues(prev => ({
        ...prev,
        appointmentTypeIds: [...currentIds, id],
        appointmentTypeLabels: { ...currentLabels, [id]: label },
      }));
    }
  };

  return (
    <Paper>
      <Stack sx={{ paddingTop: '20px' }}>
        <Title order={3}>Select Appointment Types</Title>
        <Text size={15}>Select the appointment types that trigger the flow when an athlete checks in.</Text>
        <TextInput
          placeholder='Search appointment types'
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          icon={<IconSearch size={14} />}
          rightSection={isLoading ? <Loader size={14} /> : null}
        />
        <Stack spacing={6}>
          {appointmentTypes.map(appt => {
            const isSelected = (form.values.appointmentTypeIds || []).includes(appt.value);
            return (
              <UnstyledButton key={appt.value} onClick={() => toggleAppointmentType(appt.value, appt.label)}>
                <Box className={cx(classes.card, { [classes.cardSelected]: isSelected })}>
                  <Text weight={isSelected ? 600 : 400}>{appt.label}</Text>
                </Box>
              </UnstyledButton>
            );
          })}
          {!isLoading && appointmentTypes.length === 0 && (
            <Text size='sm' color='dimmed'>
              No appointment types found
            </Text>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default KiloGLMAppointmentTypeSelect;
